<template>
  <v-dialog
    v-model="showDialog"
    max-width="600"
    persistent
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <v-card class="loan-promo-card">
      <!-- Header with animation -->
      <div class="promo-header">
        <div class="header-bg"></div>
        <div class="header-content">
          <div class="icon-wrapper">
            <v-icon size="64" color="white" class="animated-icon">monetization_on</v-icon>
          </div>
          <h2 class="promo-title">🎉 Introducing: Loan System!</h2>
          <p class="promo-subtitle">Quick and easy access to funds</p>
        </div>
      </div>

      <!-- Content -->
      <v-card-text class="pa-6">
        <div class="features-grid">
          <!-- Feature 1 -->
          <div class="feature-item">
            <div class="feature-icon-wrapper success">
              <v-icon color="success">flash_on</v-icon>
            </div>
            <div class="feature-content">
              <div class="feature-title">Fast Approval</div>
              <div class="feature-desc">Instant eligibility check</div>
            </div>
          </div>

          <!-- Feature 2 -->
          <div class="feature-item">
            <div class="feature-icon-wrapper primary">
              <v-icon color="primary">account_balance_wallet</v-icon>
            </div>
            <div class="feature-content">
              <div class="feature-title">5,000 - 50,000 FCFA</div>
              <div class="feature-desc">Flexible amounts based on your profile</div>
            </div>
          </div>

          <!-- Feature 3 -->
          <div class="feature-item">
            <div class="feature-icon-wrapper orange">
              <v-icon color="orange">schedule</v-icon>
            </div>
            <div class="feature-content">
              <div class="feature-title">15 or 25 days</div>
              <div class="feature-desc">Choose your repayment period</div>
            </div>
          </div>

          <!-- Feature 4 -->
          <div class="feature-item">
            <div class="feature-icon-wrapper purple">
              <v-icon color="purple">check_circle</v-icon>
            </div>
            <div class="feature-content">
              <div class="feature-title">Simple Criteria</div>
              <div class="feature-desc">Just one criterion needed to qualify</div>
            </div>
          </div>
        </div>

        <!-- CTA Section -->
        <v-alert
          type="info"
          border="left"
          colored-border
          class="mt-6 mb-4"
          elevation="0"
        >
          <div class="d-flex align-center">
            <v-icon left color="info">lightbulb</v-icon>
            <span class="body-2">
              Investments, savings, referrals... Find out if you're eligible!
            </span>
          </div>
        </v-alert>

        <!-- Stats Preview -->
        <div class="stats-preview">
          <div class="stat-item">
            <div class="stat-value">Instant</div>
            <div class="stat-label">Verification</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value">100%</div>
            <div class="stat-label">Secure</div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-value">0 FCFA</div>
            <div class="stat-label">Application fees</div>
          </div>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="pa-6 pt-0">
        <v-btn
          text
          color="grey"
          @click="closeDialog"
        >
          Maybe later
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          dark
          large
          depressed
          @click="goToLoanApplication"
          class="cta-button"
        >
          <v-icon left>arrow_forward</v-icon>
          Check my eligibility
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'LoanPromoPopup',

  data() {
    return {
      showDialog: false
    }
  },

  computed: {
    shouldShowPopup() {
      // Check if user is authenticated
      if (!this.$store.state.isAuthenticated) {
        return false
      }

      // Check if user has already seen this popup
      const userId = this.$store.state.user_info?.user?.id || this.$store.state.username || 'anonymous'
      const currentVersion = this.$store.state.loanPromoVersion || 1
      const seenData = this.$store.state.loanPromoSeenUsers || {}

      const userData = seenData[userId]

      // If no data or outdated version
      if (!userData || userData.version < currentVersion) {
        return true
      }

      return false
    }
  },

  mounted() {
    // Wait for user to be authenticated and data to load
    setTimeout(() => {
      if (this.shouldShowPopup) {
        this.showDialog = true
      }
    }, 2000) // 2-second delay after loading
  },

  methods: {
    closeDialog() {
      this.showDialog = false
      this.markAsSeen()
    },

    goToLoanApplication() {
      this.showDialog = false
      this.markAsSeen()
      this.$router.push('/savings/loans/create/new_loan')
    },

    markAsSeen() {
      const userId = this.$store.state.user_info?.user?.id || this.$store.state.username || 'anonymous'
      this.$store.commit('setLoanPromoSeenForUser', { userId })
    }
  }
}
</script>

<style scoped>
/* Header */
.promo-header {
  position: relative;
  padding: 48px 32px;
  text-align: center;
  overflow: hidden;
}

.header-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #00479b 0%, #0066d6 100%);
  z-index: 0;
}

.header-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.header-content {
  position: relative;
  z-index: 1;
  color: white;
}

.icon-wrapper {
  margin-bottom: 16px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.animated-icon {
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
}

.promo-title {
  font-size: 1.8rem;
  font-weight: 900;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.promo-subtitle {
  font-size: 1rem;
  opacity: 0.95;
  font-weight: 500;
}

/* Features Grid */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fc, #ffffff);
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.feature-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon-wrapper.success {
  background: rgba(27, 94, 32, 0.1);
}

.feature-icon-wrapper.primary {
  background: rgba(0, 71, 155, 0.1);
}

.feature-icon-wrapper.orange {
  background: rgba(255, 152, 0, 0.1);
}

.feature-icon-wrapper.purple {
  background: rgba(156, 39, 176, 0.1);
}

.feature-content {
  flex-grow: 1;
}

.feature-title {
  font-weight: 700;
  color: #212121;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.feature-desc {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.4;
}

/* Stats Preview */
.stats-preview {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #f1f3f8, #e8ecf4);
  border-radius: 12px;
  margin-top: 24px;
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 900;
  color: #00479b;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #cbd5e0;
}

/* CTA Button */
.cta-button {
  font-weight: 700 !important;
  text-transform: none !important;
  letter-spacing: 0;
  padding: 0 32px !important;
}

/* Mobile Responsive */
@media (max-width: 600px) {
  .promo-header {
    padding: 32px 24px;
  }

  .promo-title {
    font-size: 1.4rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .stats-preview {
    flex-direction: column;
    gap: 16px;
  }

  .stat-divider {
    width: 80%;
    height: 1px;
  }
}
.feature-icon-wrapper .v-icon {
  color: white !important;
}
</style>